import { Tag, Pencil, Eye, Copy, Clock, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Promotion } from '@/hooks/usePromotions';

interface PromotionDetailsExpandedProps {
  promotion: Promotion;
  onEdit: () => void;
  onViewProducts: () => void;
  onDuplicate: () => void;
  onEndEarly: () => void;
  onDelete: () => void;
}

export function PromotionDetailsExpanded({
  promotion,
  onEdit,
  onViewProducts,
  onDuplicate,
  onEndEarly,
  onDelete,
}: PromotionDetailsExpandedProps) {
  const locationCount = promotion.promotion_locations?.length || 0;
  const isPast = promotion.end_datetime && new Date(promotion.end_datetime) < new Date();

  return (
    <div className="flex gap-6 p-4 bg-muted/30 rounded-b-lg border-l-4 border-primary">
      {/* Left: Promotion info */}
      <div className="flex-1 space-y-3">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-primary" />
          <span className="font-semibold">{promotion.name}</span>
        </div>

        <div className="space-y-2 text-sm">
          <div>
            <span className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">
              Available on
            </span>
            <p className="mt-0.5">
              {locationCount} outlet{locationCount !== 1 ? 's' : ''}
            </p>
          </div>

          <div>
            <span className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">
              Customer target
            </span>
            <p className="mt-0.5">
              {promotion.customer_target === 'everyone'
                ? 'Available to everyone'
                : 'Exclusive to some'}
            </p>
          </div>

          {promotion.promotion_type === 'basic' && promotion.discount_type && (
            <div>
              <span className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">
                Discount
              </span>
              <p className="mt-0.5">
                {promotion.discount_type === 'percentage'
                  ? `${promotion.discount_value}% off`
                  : `$${promotion.discount_value?.toFixed(2)} off`}
                {' '}
                {promotion.product_scope === 'all' ? 'all products' : 'specific products'}
              </p>
            </div>
          )}

          {promotion.promotion_type === 'advanced' && (
            <div>
              <span className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">
                Type
              </span>
              <p className="mt-0.5">Advanced (conditional promotion)</p>
            </div>
          )}
        </div>
      </div>

      {/* Right: Action buttons */}
      <div className="flex flex-col gap-2 shrink-0">
        <Button onClick={onEdit} size="sm" className="gap-2 justify-start">
          <Pencil className="h-3.5 w-3.5" />
          Edit promotion
        </Button>
        <Button onClick={onViewProducts} variant="outline" size="sm" className="gap-2 justify-start">
          <Eye className="h-3.5 w-3.5" />
          View products
        </Button>
        <Button onClick={onDuplicate} variant="outline" size="sm" className="gap-2 justify-start">
          <Copy className="h-3.5 w-3.5" />
          Duplicate
        </Button>
        {!isPast && (
          <Button onClick={onEndEarly} variant="outline" size="sm" className="gap-2 justify-start">
            <Clock className="h-3.5 w-3.5" />
            End early...
          </Button>
        )}
        <Button onClick={onDelete} variant="outline" size="sm" className="gap-2 justify-start text-destructive hover:text-destructive">
          <Trash2 className="h-3.5 w-3.5" />
          Delete
        </Button>
      </div>
    </div>
  );
}
